import {
  AlternateGroupMutationData,
  ExerciseMetricMutationData,
  ExerciseMutationData,
  SetFullMutationData,
  SupersetMutationData,
  WorkoutAlternateGroup,
  WorkoutExercise,
  WorkoutSection,
  WorkoutSuperset,
} from '@strength/domain-workout';
import { atom } from 'jotai';
import { BlockBuilderExercise, BlockSlot } from '../types';

export type SlotsResults = {
  slots: BlockSlot[];
  rowCount: number;
};

export const inputAtom = atom<string>('');

// Exercise video URL map: libraryExerciseId -> videoUrl
export const exerciseVideoMapAtom = atom<Record<string, string>>({});

// Collapsed sheet components
export const collapsedSectionsAtom = atom<Set<string>>(new Set<string>());
export const collapsedExercisesAtom = atom<Set<string>>(new Set<string>());

export const isExerciseHidden = (
  isSectionCollapsed: boolean,
  exercise: WorkoutExercise,
  alternateGroup: WorkoutAlternateGroup | null,
  profileId: string | null,
  collapsedExercises: Set<string>,
) => {
  // Exercise is hidden if section is collapsed
  if (isSectionCollapsed) return true;

  // Exercise is hidden if main exercise in alternate group is collapsed
  if (alternateGroup) {
    const mainExercise = alternateGroup.exercises.find(
      (e) => e.sortOrder === 0,
    );
    return (
      mainExercise &&
      isExerciseCollapsed(
        mainExercise?.exercise,
        profileId,
        collapsedExercises,
      ) &&
      exercise.workoutExerciseId !== mainExercise.workoutExerciseId
    );
  }

  // otherwise, exercise is not hidden
  return false;
};

export const isExerciseCollapsed = (
  exercise: WorkoutExercise,
  profileId: string | null,
  collapsedExercises: Set<string>,
) => {
  const exerciseKey = getKeyForExerciseCollapse(exercise, profileId);
  return collapsedExercises.has(exerciseKey);
};

export const getKeyForSectionCollapse = (
  section: WorkoutSection,
  profileId: string | null,
) => {
  return `WorkoutSection:${section.workoutId}:${section.workoutSectionId}:${
    profileId ?? 'base'
  }`;
};

export const getKeyForExerciseCollapse = (
  exercise: WorkoutExercise,
  profileId: string | null,
) => {
  return `WorkoutExercise:${exercise.workoutId}:${exercise.workoutExerciseId}:${
    profileId ?? 'base'
  }`;
};

export const blockSectionAndExerciseLabelStyle =
  'absolute top-0 bottom-0 right-0 left-0 z-10 flex h-full w-full items-center px-2.5 py-2';

// Shared between the block builder and the workout template editor — both
// swap an exercise in place while carrying over its sets, metrics, and
// superset/alternate-group membership onto the replacement.
export const buildSwapExerciseArgs = (
  section: WorkoutSection,
  exercise: WorkoutExercise,
  superset: WorkoutSuperset | null,
  alternateGroup: WorkoutAlternateGroup | null,
  swapExercise: BlockBuilderExercise,
): {
  workoutId: string;
  workoutSectionId: string;
  previousWorkoutExerciseId: string;
  data: ExerciseMutationData;
  metrics: ExerciseMetricMutationData[];
  sets: SetFullMutationData[];
  superset: SupersetMutationData;
  alternateGroup: AlternateGroupMutationData;
} => {
  const swappableSets = exercise.sets.map((set) => ({
    workoutSetId: set.workoutSetId,
    sortOrder: set.sortOrder,
    metrics: set.metrics,
    workoutSetGroupId: set.workoutSetGroupId,
  }));

  const swappableMetrics = exercise.metrics.map((m) => ({
    metricId: m.metricId,
    metricType: m.metricType,
    metricName: m.metricName,
    sortOrder: m.sortOrder,
  }));

  const supersetExercise = superset?.exercises.find(
    (e) => e.workoutExerciseId === exercise.workoutExerciseId,
  );
  const alternateExercise = alternateGroup?.exercises.find(
    (e) => e.workoutExerciseId === exercise.workoutExerciseId,
  );

  return {
    workoutId: section.workoutId,
    workoutSectionId: section.workoutSectionId,
    previousWorkoutExerciseId: exercise.workoutExerciseId,
    data: {
      libraryExerciseId: swapExercise.id,
      exerciseName: swapExercise.name,
      instruction: exercise.instruction || null,
      sortOrder: exercise.sortOrder,
      isEachSide: exercise.isEachSide ?? false,
    },
    metrics: swappableMetrics,
    sets: swappableSets,
    superset: {
      workoutSupersetId: superset?.workoutSupersetId,
      sortOrder: supersetExercise?.sortOrder ?? 0,
    },
    alternateGroup: {
      workoutAlternateGroupId: alternateGroup?.workoutAlternateGroupId,
      sortOrder: alternateExercise?.sortOrder ?? 0,
    },
  };
};
